import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Rating,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  LinearProgress,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepButton
} from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const BEHAVIORAL_COMPETENCIES = [
  'Communication (Ability to convey thoughts and feeling with clarity)',
  'Knowledge (Job knowledge, Skills, Competencies & Application)',
  'Quality of Work (Accuracy, Precision, Reliability, less need to re-work)',
  'On-Time Delivery (Delivery as estimated)',
  'Efficiency of Work (turn around time, ability to manage multiple tasks)',
  'Ability to follow process (standards/directions)',
  'Ownership/Accountability',
  'Problem Solving (fire fighting, handling situations, raising escalations timely)',
  'Approach to Work (Initiative, Planning, Following Instructions)',
  'Interpersonal Skills (Interaction with co-workers, superiors, contribution and commitment to team success)'
];

const OPEN_ENDED_QUESTIONS = [
  'Feel free to Share your views if you need any support from the company or any cross functional teams.',
  'What are the key goals and responsibilities you perceive as most significant for you in the upcoming year?',
  "What sort of training/experiences would benefit you in the next year? Not just job-skills - also your natural strengths and personal passions you'd like to develop - you and your work can benefit from these. CHOOSE THOSE AREAS WHERE YOU FEEL YOU NEED TO IMPROVE"
];

const ManagerPerformanceReview = () => {
  const { getAuthHeaders } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [ratings, setRatings] = useState({});
  const [kraComments, setKraComments] = useState({});
  const [overallComment, setOverallComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Meeting scheduling states
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [schedulingMeeting, setSchedulingMeeting] = useState(false);
  
  // Post-meeting documentation states
  const [managerCommitments, setManagerCommitments] = useState('');
  const [employeeCommitments, setEmployeeCommitments] = useState('');
  const [finalReviewDecision, setFinalReviewDecision] = useState('');
  const [overallFeedback, setOverallFeedback] = useState('');
  const [conductingMeeting, setConductingMeeting] = useState(false);
  const [activeKraTab, setActiveKraTab] = useState(0);
  const [behavioralRatings, setBehavioralRatings] = useState({});
  const [openEndedAnswers, setOpenEndedAnswers] = useState({});

  // Manager-only evaluation fields
  const [integrityTowardsWork, setIntegrityTowardsWork] = useState(false);
  const [pipNeeded, setPipNeeded] = useState(false);
  const [culturalFit, setCulturalFit] = useState(false);
  const [goalAlignment, setGoalAlignment] = useState('');
  const [promotionDecision, setPromotionDecision] = useState(false);
  const [incrementSuggestion, setIncrementSuggestion] = useState('');
  const [roleEnhancementSuggestions, setRoleEnhancementSuggestions] = useState('');
  const [trainingDevelopmentNeeded, setTrainingDevelopmentNeeded] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  const WIZARD_STEPS = ['Overview', 'Performance', 'Competencies', 'Wrap-up'];

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/performance/manager/reviews`, {
        headers: getAuthHeaders()
      });
      setReviews(response.data.reviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to fetch performance reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReview = async (reviewId) => {
    try {
      // Add timestamp to force fresh data and bypass cache
      const response = await axios.get(`${API_URL}/performance/reviews/${reviewId}?t=${Date.now()}`, {
        headers: {
          ...getAuthHeaders(),
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      const review = response.data.review;
      setSelectedReview(review);

      // Debug: Log the status
      console.log('Review Status:', review.status);
      console.log('Manager Submitted:', review.managerSubmitted);
      console.log('Full Review:', review);

      // Initialize ratings from existing manager ratings or create new structure
      const initialRatings = {};
      const initialKraComments = {};
      if (review.form && review.form.kras) {
        review.form.kras.forEach((kra, kraIndex) => {
          // Get KRA comment from first KPI's comment (if exists)
          if (kra.kpis.length > 0) {
            const firstKpiIndex = 0;
            const firstKpiRating = review.managerRatings?.find(
              r => r.kraIndex === kraIndex && r.kpiIndex === firstKpiIndex
            );
            if (firstKpiRating && firstKpiRating.comment) {
              initialKraComments[kraIndex] = firstKpiRating.comment;
            }
          }
          
          kra.kpis.forEach((kpi, kpiIndex) => {
            const key = `${kraIndex}-${kpiIndex}`;
            const existingRating = review.managerRatings?.find(
              r => r.kraIndex === kraIndex && r.kpiIndex === kpiIndex
            );
            initialRatings[key] = existingRating ? existingRating.rating : null;
          });
        });
      }
      setRatings(initialRatings);
      setKraComments(initialKraComments);
      setOverallComment(review.managerOverallComment || '');
      setActiveKraTab(0);

      // Initialize behavioural ratings
      const initialBehavioral = {};
      if (review.managerBehavioralRatings) {
        review.managerBehavioralRatings.forEach(br => {
          initialBehavioral[br.competencyIndex] = br.rating;
        });
      }
      setBehavioralRatings(initialBehavioral);

      // Initialize open-ended answers
      const initialOpenEnded = {};
      if (review.managerOpenEndedAnswers) {
        review.managerOpenEndedAnswers.forEach(oea => {
          initialOpenEnded[oea.questionIndex] = oea.answer;
        });
      }
      setOpenEndedAnswers(initialOpenEnded);

      // Land on the Wrap-up step when a meeting action / documentation is in focus
      const meetingPhase = ['meeting_scheduling_pending', 'reschedule_required', 'meeting_scheduled', 'meeting_accepted', 'completed'].includes(review.status);
      setActiveStep(meetingPhase ? WIZARD_STEPS.length - 1 : 0);
    } catch (error) {
      console.error('Error fetching review:', error);
      toast.error('Failed to fetch performance review');
    }
  };

  const handleRatingChange = (kraIndex, kpiIndex, value) => {
    const key = `${kraIndex}-${kpiIndex}`;
    setRatings({ ...ratings, [key]: value });
  };

  const handleSubmit = async () => {
    if (!selectedReview) return;

    // Validate all ratings are provided
    const form = selectedReview.form;
    const missingRatings = [];
    
    form.kras.forEach((kra, kraIndex) => {
      kra.kpis.forEach((kpi, kpiIndex) => {
        const key = `${kraIndex}-${kpiIndex}`;
        if (!ratings[key] || ratings[key] < 1 || ratings[key] > 5) {
          missingRatings.push(`${kra.name} - ${kpi.name}`);
        }
      });
    });

    if (missingRatings.length > 0) {
      toast.error('Please provide ratings for all KPIs');
      return;
    }

    // Validate all 10 behavioural competency ratings
    for (let i = 0; i < 10; i++) {
      if (!behavioralRatings[i] || behavioralRatings[i] < 1) {
        toast.error('Please provide ratings for all Behavioural Competencies');
        return;
      }
    }

    // Convert ratings to the required format
    const ratingArray = [];
    form.kras.forEach((kra, kraIndex) => {
      const kraComment = kraComments[kraIndex] || '';
      kra.kpis.forEach((kpi, kpiIndex) => {
        const key = `${kraIndex}-${kpiIndex}`;
        // Store KRA comment in the first KPI's comment field
        ratingArray.push({
          kraIndex,
          kpiIndex,
          rating: ratings[key],
          comment: kpiIndex === 0 ? kraComment : ''
        });
      });
    });

    const behavioralArray = Object.entries(behavioralRatings).map(([idx, rating]) => ({
      competencyIndex: parseInt(idx),
      rating
    }));

    const openEndedArray = Object.entries(openEndedAnswers).map(([idx, answer]) => ({
      questionIndex: parseInt(idx),
      answer
    }));

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}/manager-rating`,
        {
          ratings: ratingArray,
          overallComment,
          behavioralRatings: behavioralArray,
          openEndedAnswers: openEndedArray,
          managerEvaluation: {
            integrityTowardsWork,
            pipNeeded,
            culturalFit,
            goalAlignment,
            promotionDecision,
            incrementSuggestion,
            roleEnhancementSuggestions,
            trainingDevelopmentNeeded
          }
        },
        {
          headers: getAuthHeaders()
        }
      );
      toast.success('Manager rating submitted successfully! Please schedule an appraisal meeting.');
      
      // Refresh the current review to show meeting scheduling UI
      await handleViewReview(selectedReview.reviewId);
      fetchReviews(); // Update the list in background
      
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error(error.response?.data?.message || 'Failed to submit manager rating');
    } finally {
      setSubmitting(false);
    }
  };

  const handleScheduleMeeting = async () => {
    if (!meetingDate || !meetingTime) {
      toast.error('Please select both date and time for the meeting');
      return;
    }

    setSchedulingMeeting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}/schedule-meeting`,
        {
          proposedDate: meetingDate,
          proposedTime: meetingTime
        },
        {
          headers: getAuthHeaders()
        }
      );
      toast.success('Meeting scheduled successfully. Employee will be notified.');
      fetchReviews();
      const updatedReview = await axios.get(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}`,
        { headers: getAuthHeaders() }
      );
      setSelectedReview(updatedReview.data.review);
      setMeetingDate('');
      setMeetingTime('');
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast.error(error.response?.data?.message || 'Failed to schedule meeting');
    } finally {
      setSchedulingMeeting(false);
    }
  };

  const handleConductMeeting = async () => {
    if (!managerCommitments || !employeeCommitments || !finalReviewDecision || !overallFeedback) {
      toast.error('All post-meeting fields are required');
      return;
    }

    setConductingMeeting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}/conduct-meeting`,
        {
          managerCommitments,
          employeeCommitments,
          finalReviewDecision,
          overallFeedback
        },
        {
          headers: getAuthHeaders()
        }
      );
      toast.success('Meeting marked as conducted. Appraisal process completed!');
      setSelectedReview(null);
      fetchReviews();
    } catch (error) {
      console.error('Error conducting meeting:', error);
      toast.error(error.response?.data?.message || 'Failed to mark meeting as conducted');
    } finally {
      setConductingMeeting(false);
    }
  };

  const getStatusLabel = (review) => {
    switch (review.status) {
      case 'completed':
        return 'Completed';
      case 'meeting_conducted':
        return 'Meeting Conducted';
      case 'meeting_accepted':
        return 'Meeting Accepted';
      case 'meeting_scheduled':
        return 'Meeting Scheduled';
      case 'meeting_scheduling_pending':
        return 'Schedule Meeting';
      case 'reschedule_required':
        return 'Reschedule Required';
      case 'manager_submitted':
        return 'Submitted';
      case 'employee_submitted':
        return 'Pending Your Rating';
      default:
        return 'Waiting for Employee';
    }
  };

  const getStatusColor = (review) => {
    switch (review.status) {
      case 'completed':
      case 'meeting_conducted':
        return 'success';
      case 'meeting_accepted':
        return 'info';
      case 'meeting_scheduled':
      case 'meeting_scheduling_pending':
        return 'warning';
      case 'reschedule_required':
        return 'error';
      case 'manager_submitted':
        return 'info';
      case 'employee_submitted':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress />
      </Box>
    );
  }

  if (selectedReview) {
    const form = selectedReview.form;
    const isSubmitted = selectedReview.managerSubmitted;
    const employeeSubmitted = selectedReview.employeeSubmitted;
    const isCompleted = selectedReview.status === 'completed';
    const needsScheduling = selectedReview.status === 'meeting_scheduling_pending';
    const meetingScheduled = selectedReview.status === 'meeting_scheduled';
    const needsReschedule = selectedReview.status === 'reschedule_required';
    const meetingAccepted = selectedReview.status === 'meeting_accepted';
    const meetingConducted = selectedReview.appraisalMeeting?.meetingConducted;

    // Per-step completion (used for stepper check marks while the manager is rating)
    const canRate = !isSubmitted && employeeSubmitted;
    const totalKpisCount = form.kras.reduce((s, k) => s + k.kpis.length, 0);
    const ratedKpisCount = Object.values(ratings).filter(Boolean).length;
    const behavioralDone = BEHAVIORAL_COMPETENCIES.every((_, i) => behavioralRatings[i]);
    const stepComplete = [
      true,
      totalKpisCount > 0 && ratedKpisCount === totalKpisCount,
      behavioralDone,
      !!overallComment
    ];
    const goNext = () => setActiveStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1));
    const goBack = () => setActiveStep((s) => Math.max(s - 1, 0));
    const isLastStep = activeStep === WIZARD_STEPS.length - 1;

    return (
      <Box p={3}>
        <Button
          variant="outlined"
          onClick={() => setSelectedReview(null)}
          sx={{ mb: 2 }}
        >
          ← Back to Reviews
        </Button>

        {/* Wizard stepper — one section at a time */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <Stepper activeStep={activeStep} alternativeLabel nonLinear>
              {WIZARD_STEPS.map((label, i) => (
                <Step key={label} completed={canRate && stepComplete[i]}>
                  <StepButton color="inherit" onClick={() => setActiveStep(i)}>
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* STEP 0 — Overview */}
        {activeStep === 0 && (
        <>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Review Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Employee</Typography>
                <Typography variant="body1">
                  {selectedReview.employee?.firstName} {selectedReview.employee?.lastName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedReview.employee?.email}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Review Cycle</Typography>
                <Typography variant="body1">{form.reviewCycle} {form.reviewYear}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip
                  label={isCompleted ? 'Completed' : isSubmitted ? 'Submitted' : employeeSubmitted ? 'Pending Your Rating' : 'Waiting for Employee'}
                  color={isCompleted ? 'success' : isSubmitted ? 'info' : employeeSubmitted ? 'warning' : 'default'}
                />
              </Grid>
              {isCompleted && selectedReview.calculatedScores && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Final Score</Typography>
                  <Typography variant="h6" color="primary">
                    {selectedReview.calculatedScores.finalScore.toFixed(2)}/5.00
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {!employeeSubmitted && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Waiting for employee to submit their self-assessment.
          </Alert>
        )}

        {isCompleted && selectedReview.calculatedScores && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Performance Score Breakdown</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>KRA</TableCell>
                      <TableCell align="right">Weightage</TableCell>
                      <TableCell align="right">Average Score</TableCell>
                      <TableCell align="right">Weighted Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedReview.calculatedScores.kraAverages.map((kraAvg) => {
                      const kra = form.kras[kraAvg.kraIndex];
                      const weightedScore = selectedReview.calculatedScores.weightedScores.find(
                        ws => ws.kraIndex === kraAvg.kraIndex
                      );
                      return (
                        <TableRow key={kraAvg.kraIndex}>
                          <TableCell>{kra.name}</TableCell>
                          <TableCell align="right">{kra.weightage}%</TableCell>
                          <TableCell align="right">{kraAvg.average.toFixed(2)}</TableCell>
                          <TableCell align="right">{weightedScore?.wts.toFixed(2)}</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell><strong>Final Performance Score</strong></TableCell>
                      <TableCell align="right"></TableCell>
                      <TableCell align="right"></TableCell>
                      <TableCell align="right">
                        <strong>{selectedReview.calculatedScores.finalScore.toFixed(2)}</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
        </>
        )}

        {/* STEP 1 — Performance Review (card-based section navigation) */}
        {activeStep === 1 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Performance Review Details
            </Typography>

            {/* Rating progress bar (only when manager can rate) */}
            {!isSubmitted && employeeSubmitted && (() => {
              const totalKpis = form.kras.reduce((sum, kra) => sum + kra.kpis.length, 0);
              const ratedKpis = Object.values(ratings).filter(Boolean).length;
              return (
                <Box sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" color="textSecondary">Rating Progress</Typography>
                    <Typography variant="caption" color="textSecondary">{ratedKpis} / {totalKpis} KPIs rated</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={(ratedKpis / totalKpis) * 100} sx={{ borderRadius: 1 }} />
                </Box>
              );
            })()}

            {/* KRA tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={activeKraTab}
                onChange={(e, v) => setActiveKraTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                textColor="primary"
                indicatorColor="primary"
              >
                {form.kras.map((kra, i) => {
                  const allRated = !isSubmitted && employeeSubmitted && kra.kpis.every((_, j) => ratings[`${i}-${j}`]);
                  return (
                    <Tab
                      key={i}
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <span>{i + 1}. {kra.name.length > 18 ? kra.name.substring(0, 18) + '…' : kra.name}</span>
                          {allRated && <span style={{ color: '#2e7d32', fontSize: 13, fontWeight: 700 }}>✓</span>}
                        </Box>
                      }
                    />
                  );
                })}
              </Tabs>
            </Box>

            {/* Active KRA section */}
            {form.kras.map((kra, kraIndex) =>
              activeKraTab !== kraIndex ? null : (
                <Box key={kraIndex}>
                  {/* Compact KRA header */}
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '50%',
                      bgcolor: 'primary.main', color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, flexShrink: 0
                    }}>
                      {kraIndex + 1}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">{kra.name}</Typography>
                      {kra.description && (
                        <Typography variant="caption" color="textSecondary">{kra.description}</Typography>
                      )}
                    </Box>
                  </Box>

                  {/* KPI cards — 2-column compact grid */}
                  <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                    {kra.kpis.map((kpi, kpiIndex) => {
                      const key = `${kraIndex}-${kpiIndex}`;
                      const currentRating = ratings[key];
                      const existingRating = selectedReview.managerRatings?.find(
                        r => r.kraIndex === kraIndex && r.kpiIndex === kpiIndex
                      );
                      const employeeRating = selectedReview.employeeRatings?.find(
                        r => r.kraIndex === kraIndex && r.kpiIndex === kpiIndex
                      );
                      return (
                        <Grid item xs={12} sm={6} key={kpiIndex}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              borderLeft: '3px solid',
                              borderLeftColor: (!isSubmitted && employeeSubmitted && currentRating) ? 'success.main' : 'primary.light',
                              bgcolor: (!isSubmitted && employeeSubmitted && currentRating) ? 'success.50' : 'background.paper',
                              transition: 'all 0.2s',
                              height: '100%'
                            }}
                          >
                            <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                              <Box sx={{
                                width: 20, height: 20, borderRadius: '50%',
                                bgcolor: 'grey.200', color: 'text.secondary',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, fontWeight: 700, flexShrink: 0, mt: 0.2
                              }}>
                                {kpiIndex + 1}
                              </Box>
                              <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.4 }}>
                                {kpi.name}
                              </Typography>
                            </Box>

                            {/* Ratings row */}
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                              {/* Employee rating */}
                              {(isCompleted || isSubmitted) && employeeRating && (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <Typography variant="caption" color="textSecondary" sx={{ minWidth: 52 }}>Emp:</Typography>
                                  <Rating value={employeeRating.rating || 0} readOnly max={5} size="small" />
                                  <Typography variant="caption">{employeeRating.rating ?? 'N/A'}</Typography>
                                </Box>
                              )}
                              {/* Manager rating */}
                              {!isSubmitted && employeeSubmitted ? (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <Rating
                                    value={currentRating || 0}
                                    onChange={(event, newValue) => handleRatingChange(kraIndex, kpiIndex, newValue)}
                                    max={5}
                                    size="small"
                                  />
                                  <Typography variant="caption" color={currentRating ? 'success.main' : 'text.disabled'}>
                                    {currentRating ? `${currentRating}/5` : '—'}
                                  </Typography>
                                </Box>
                              ) : isSubmitted ? (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <Typography variant="caption" color="textSecondary" sx={{ minWidth: 52 }}>Mgr:</Typography>
                                  <Rating value={existingRating?.rating || 0} readOnly max={5} size="small" />
                                  <Typography variant="caption">{existingRating?.rating ?? 'N/A'}</Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.disabled">Waiting for employee</Typography>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* KRA comment — compact */}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="textSecondary" fontWeight="medium" sx={{ mb: 0.5, display: 'block' }}>
                      Section Comment
                    </Typography>
                    {!isSubmitted && employeeSubmitted ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        value={kraComments[kraIndex] || ''}
                        onChange={(e) => setKraComments({ ...kraComments, [kraIndex]: e.target.value })}
                        placeholder={`Optional comments for ${kra.name}...`}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ fontStyle: kraComments[kraIndex] ? 'normal' : 'italic' }}>
                        {kraComments[kraIndex] || 'No comment provided'}
                      </Typography>
                    )}
                  </Box>

                  {/* Prev / Next */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button size="small" variant="text" onClick={() => setActiveKraTab(activeKraTab - 1)} disabled={activeKraTab === 0}>
                      ← Prev
                    </Button>
                    <Typography variant="caption" color="textSecondary">
                      {activeKraTab + 1} / {form.kras.length}
                    </Typography>
                    <Button size="small" variant="text" onClick={() => setActiveKraTab(activeKraTab + 1)} disabled={activeKraTab === form.kras.length - 1}>
                      Next →
                    </Button>
                  </Box>
                </Box>
              )
            )}
          </CardContent>
        </Card>
        )}

        {/* STEP 2 — Behavioural Competencies */}
        {activeStep === 2 && (
          ((!isSubmitted && employeeSubmitted) || isSubmitted || isCompleted) ? (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Behavioural Competencies</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Rate each competency on a scale of 1–5. This section is not included in the final performance score.
              </Typography>
              <Grid container spacing={1.5}>
                {BEHAVIORAL_COMPETENCIES.map((competency, index) => {
                  const currentRating = behavioralRatings[index];
                  const myRating = selectedReview.managerBehavioralRatings?.find(br => br.competencyIndex === index);
                  const empRating = selectedReview.employeeBehavioralRatings?.find(br => br.competencyIndex === index);
                  return (
                    <Grid item xs={12} sm={6} key={index}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          borderLeft: '3px solid',
                          borderLeftColor: (!isSubmitted && employeeSubmitted && currentRating) ? 'success.main' : 'secondary.light',
                          bgcolor: (!isSubmitted && employeeSubmitted && currentRating) ? 'success.50' : 'background.paper',
                          transition: 'all 0.2s',
                          height: '100%'
                        }}
                      >
                        <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                          <Box sx={{
                            width: 20, height: 20, borderRadius: '50%',
                            bgcolor: 'grey.200', color: 'text.secondary',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 10, fontWeight: 700, flexShrink: 0, mt: 0.2
                          }}>
                            {index + 1}
                          </Box>
                          <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.4 }}>
                            {competency}
                          </Typography>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {isCompleted && empRating && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="caption" color="textSecondary" sx={{ minWidth: 52 }}>Emp:</Typography>
                              <Rating value={empRating.rating || 0} readOnly max={5} size="small" />
                              <Typography variant="caption">{empRating.rating ?? 'N/A'}</Typography>
                            </Box>
                          )}
                          {!isSubmitted && employeeSubmitted ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Rating
                                value={currentRating || 0}
                                onChange={(_, v) => setBehavioralRatings({ ...behavioralRatings, [index]: v })}
                                max={5}
                                size="small"
                              />
                              <Typography variant="caption" color={currentRating ? 'success.main' : 'text.disabled'}>
                                {currentRating ? `${currentRating}/5` : '—'}
                              </Typography>
                            </Box>
                          ) : isSubmitted || isCompleted ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="caption" color="textSecondary" sx={{ minWidth: 52 }}>Mgr:</Typography>
                              <Rating value={myRating?.rating || 0} readOnly max={5} size="small" />
                              <Typography variant="caption">{myRating?.rating ?? 'N/A'}</Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">Waiting for employee</Typography>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
          ) : (
            <Alert severity="info">Behavioural competencies will be available once the employee submits their self-assessment.</Alert>
          )
        )}


        {/* STEP 3 — Wrap-up: comments, manager evaluation, meeting & submission */}
        {activeStep === 3 && (
        <>
        {!isSubmitted && employeeSubmitted && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Overall Comments</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={overallComment}
                onChange={(e) => setOverallComment(e.target.value)}
                placeholder="Add your comments about the employee's performance..."
              />
            </CardContent>
          </Card>
        )}

        {isCompleted && selectedReview.employeeOverallComment && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Employee's Comments</Typography>
              <Typography variant="body1">{selectedReview.employeeOverallComment}</Typography>
            </CardContent>
          </Card>
        )}

        {isCompleted && selectedReview.managerOverallComment && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Your Comments</Typography>
              <Typography variant="body1">{selectedReview.managerOverallComment}</Typography>
            </CardContent>
          </Card>
        )}

        {/* Meeting Scheduling Section */}
        {(needsScheduling || needsReschedule) && (
          <Card sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {needsReschedule ? '⚠️ Reschedule Meeting Required' : '📅 Schedule Appraisal Meeting'}
              </Typography>
              {needsReschedule && selectedReview.appraisalMeeting?.employeeResponse && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Employee declined the meeting:</strong>
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Reason:</strong> {selectedReview.appraisalMeeting.employeeResponse.declineReason}
                  </Typography>
                  {selectedReview.appraisalMeeting.employeeResponse.alternateDate && (
                    <Typography variant="body2">
                      <strong>Suggested Alternate:</strong>{' '}
                      {new Date(selectedReview.appraisalMeeting.employeeResponse.alternateDate).toLocaleDateString()}{' '}
                      at {selectedReview.appraisalMeeting.employeeResponse.alternateTime}
                    </Typography>
                  )}
                </Alert>
              )}
              <Typography variant="body2" sx={{ mb: 2 }}>
                Please schedule a meeting to discuss the appraisal with the employee. This is mandatory to complete the review.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Meeting Date"
                    type="date"
                    value={meetingDate}
                    onChange={(e) => setMeetingDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: new Date().toISOString().split('T')[0] }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Meeting Time"
                    type="time"
                    value={meetingTime}
                    onChange={(e) => setMeetingTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleScheduleMeeting}
                  disabled={schedulingMeeting || !meetingDate || !meetingTime}
                  color="primary"
                >
                  {schedulingMeeting ? <CircularProgress size={24} /> : 'Schedule Meeting'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Meeting Status - Waiting for Employee Response */}
        {meetingScheduled && !meetingAccepted && (
          <Card sx={{  mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'info.dark' }}>
                ⏳ Waiting for Employee Confirmation
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Scheduled Date:</strong> {new Date(selectedReview.appraisalMeeting.proposedDate).toLocaleDateString()}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Scheduled Time:</strong> {selectedReview.appraisalMeeting.proposedTime}
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                The employee has been notified. Waiting for their confirmation.
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* Completed Appraisal - Show Documentation */}
        {isCompleted && meetingConducted && selectedReview.appraisalMeeting && (
          <Card sx={{ mb: 3,  border: '2px solid', borderColor: 'success.main' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'success.dark' }}>
                ✅ Appraisal Process Completed
              </Typography>
              <Alert severity="success" sx={{ mb: 2 }}>
                This appraisal has been completed and locked. The meeting was conducted on{' '}
                {new Date(selectedReview.appraisalMeeting.conductedAt).toLocaleDateString()}.
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Meeting Date</Typography>
                  <Typography variant="body1">{new Date(selectedReview.appraisalMeeting.proposedDate).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Meeting Time</Typography>
                  <Typography variant="body1">{selectedReview.appraisalMeeting.proposedTime}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Manager Commitments</Typography>
                  <Typography variant="body1" paragraph>{selectedReview.appraisalMeeting.managerCommitments}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Employee Commitments</Typography>
                  <Typography variant="body1" paragraph>{selectedReview.appraisalMeeting.employeeCommitments}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Final Review Decision</Typography>
                  <Typography variant="body1" paragraph>{selectedReview.appraisalMeeting.finalReviewDecision}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Overall Feedback / Summary</Typography>
                  <Typography variant="body1">{selectedReview.appraisalMeeting.overallFeedback}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Manager-Only Evaluation — not visible to employees */}
        {!isSubmitted && employeeSubmitted && (
          <Card sx={{ mb: 3, border: '1px solid', borderColor: 'warning.light' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Manager Evaluation</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                This section is for managers only and is not visible to the employee.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Integrity of the employee towards work</Typography>
                    <FormControlLabel
                      control={<Switch checked={integrityTowardsWork} onChange={(e) => setIntegrityTowardsWork(e.target.checked)} />}
                      label={integrityTowardsWork ? 'Y' : 'N'}
                      labelPlacement="start"
                      sx={{ mr: 0 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Performance Improvement Plan Needed</Typography>
                    <FormControlLabel
                      control={<Switch checked={pipNeeded} onChange={(e) => setPipNeeded(e.target.checked)} />}
                      label={pipNeeded ? 'Y' : 'N'}
                      labelPlacement="start"
                      sx={{ mr: 0 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Employee is culturally fit with the organisation</Typography>
                    <FormControlLabel
                      control={<Switch checked={culturalFit} onChange={(e) => setCulturalFit(e.target.checked)} />}
                      label={culturalFit ? 'Y' : 'N'}
                      labelPlacement="start"
                      sx={{ mr: 0 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Promotion Decision</Typography>
                    <FormControlLabel
                      control={<Switch checked={promotionDecision} onChange={(e) => setPromotionDecision(e.target.checked)} />}
                      label={promotionDecision ? 'Y' : 'N'}
                      labelPlacement="start"
                      sx={{ mr: 0 }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="% Employee is aligned with Company Goals"
                    value={goalAlignment}
                    onChange={(e) => setGoalAlignment(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Suggestion on Increment %"
                    value={incrementSuggestion}
                    onChange={(e) => setIncrementSuggestion(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Role Enhancement Suggestions"
                    value={roleEnhancementSuggestions}
                    onChange={(e) => setRoleEnhancementSuggestions(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Training & Development Needed"
                    value={trainingDevelopmentNeeded}
                    onChange={(e) => setTrainingDevelopmentNeeded(e.target.value)}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
        </>
        )}

        {/* Wizard navigation */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={goBack} disabled={activeStep === 0}>
            ← Back
          </Button>
          <Typography variant="body2" color="textSecondary">
            Step {activeStep + 1} of {WIZARD_STEPS.length} — {WIZARD_STEPS[activeStep]}
          </Typography>
          {!isLastStep ? (
            <Button variant="contained" onClick={goNext}>
              Next →
            </Button>
          ) : canRate ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : 'Submit Manager Rating'}
            </Button>
          ) : (
            <Button variant="outlined" onClick={() => setSelectedReview(null)}>
              Back to Reviews
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Performance Reviews - Manager View</Typography>
      
      {reviews.length === 0 ? (
        <Alert severity="info">No performance reviews assigned to you.</Alert>
      ) : (
        <Grid container spacing={3}>
          {reviews.map((review) => (
            <Grid item xs={12} md={6} key={review._id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {review.formId?.title || 'Performance Review'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {review.formId?.reviewCycle} {review.formId?.reviewYear}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Employee: {review.employeeId?.firstName} {review.employeeId?.lastName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {review.employeeId?.email}
                  </Typography>
                  <Box mt={2}>
                    <Chip
                      label={getStatusLabel(review)}
                      color={getStatusColor(review)}
                      sx={{ mr: 1 }}
                    />
                    {review.calculatedScores?.finalScore && (
                      <Chip
                        label={`Score: ${review.calculatedScores.finalScore.toFixed(2)}/5.00`}
                        color="primary"
                      />
                    )}
                  </Box>
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      onClick={() => handleViewReview(review._id)}
                      disabled={!review.employeeSubmitted && review.status !== 'completed'}
                      fullWidth={false}
                      sx={{ mr: 1 }}
                    >
                      View Details
                    </Button>
                    {(review.status === 'meeting_scheduling_pending' || review.status === 'reschedule_required') && (
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={() => handleViewReview(review._id)}
                        startIcon={<span>📅</span>}
                      >
                        Schedule Meeting
                      </Button>
                    )}
                    {review.status === 'meeting_scheduled' && (
                      <Button
                        variant="outlined"
                        color="info"
                        onClick={() => handleViewReview(review._id)}
                        startIcon={<span>⏳</span>}
                      >
                        Awaiting Response
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ManagerPerformanceReview;

